import classNames from "classnames";
import { isEmpty } from "lodash";
import * as React from "react";
import { CSSProperties } from "react";
import * as ReactDOM from "react-dom";
import { Keys } from "~/common/enums";
import { AdjustPositionOptions } from "~/EasyProjectsCommon/General";
import { getScrollParent } from "~/EasyProjectsCommon/getScrollParent";
import { ButtonContrastIcon } from "~/sharedComponents/buttons";
import { DatePresenter } from "~/sharedComponents/date/DatePresenter";
import { icon } from "~/sharedComponents/icons";
import { VerticalScrollbars } from "~/sharedComponents/scrollbars/VerticalScrollbars";
import { AdjustPosition } from "~/sharedHOC/adjustContainer/AdjustPosition";
import { ClickOutside } from "~/sharedHOC/ClickOutside/ClickOutside";
import { WithKeyHandlers } from "~/sharedHOC/WithKeyHandlers/WithKeyHandlers";
import { ProjectOrTask } from "../../types";
import { clearAnnotation, deleteAnnotation, mouseEnterAnnotation, mouseLeaveAnnotation } from "../actions";
import { AnnotationTextAreaContainer } from "../containers/AnnotationTextAreaContainer";
import { AnnotationItemExtended } from "../types";
import { FormattedMessage } from "./FormattedMessage";
import { ImageAnnotationMenu } from "./ImageAnnotationMenu";
import * as styles from "./ImageAnnotationsEditor.less";

const messageScrollbarStyles: React.CSSProperties = {
    width: "100%",
    minHeight: "20px",
    maxHeight: "161px",
};

const classNamesCtx = classNames.bind(styles);

export interface AnnotationContentState {
    openMenu: boolean;
}

export interface AnnotationContentProps {
    onChange: F1<AnnotationItemExtended, void>;
    onCancel: F<void>; // typeof clearAnnotation;
    onSubmit: F<void>;
    annotation: AnnotationItemExtended;
    theme?: AnnotationContentTheme[] | AnnotationContentTheme;
    startEdit: F1<AnnotationItemExtended, void>;
    active?: boolean;
    editingAnnotation: AnnotationItemExtended;
    deleteAnnotation: typeof deleteAnnotation;
    setAnnotation: F1<AnnotationItemExtended, void>;
    clearAnnotation: typeof clearAnnotation;
    isInlineEdit?: boolean;
    mouseEnterAnnotation?: typeof mouseEnterAnnotation;
    mouseLeaveAnnotation?: typeof mouseLeaveAnnotation;
    isParentScrolling?: boolean;
    menuRefCallback?: F1<React.RefObject<HTMLElement>, void>;
    attachmentId?: number;
    projectOrTask: ProjectOrTask;
}

export enum AnnotationContentTheme {
    light = "light",
    dark = "dark",
    inner_tooltip = "inner_tooltip",
    inner_message = "inner_message",
}

const adjustMenuOptions: AdjustPositionOptions = {
    isBody: true,
    indent: {
        x: 8,
    },
    forceRightAlign: true,
    underBaseElement: true,
};

export const adjustContentOptions: AdjustPositionOptions = {
    isBody: false,
    indent: {
        x: 28,
    },
    viewport: "[data-ep-img-viewport]",
    adjustedAbove: styles.adjusted_above,
    adjustedRight: styles.adjusted_right,
};

export class AnnotationContent extends React.Component<AnnotationContentProps, AnnotationContentState> {
    commentRef = React.createRef<HTMLDivElement>();
    menuRef = React.createRef<HTMLDivElement>();
    menuContainerRef = React.createRef<HTMLDivElement>();
    menuWrapperRef = React.createRef<HTMLDivElement>();
    menuOpenerRef = React.createRef<HTMLDivElement>();
    adjustPositionRef = React.createRef<AdjustPosition>();
    containerRef = React.createRef<HTMLDivElement>();
    fakeDotRef = React.createRef<HTMLDivElement>();

    constructor(props: AnnotationContentProps) {
        super(props);
        this.state = {
            openMenu: false,
        };
        this.onClickMenu = this.onClickMenu.bind(this);
        this.onEscDown = this.onEscDown.bind(this);
        this.onMouseEnter = this.onMouseEnter.bind(this);
        this.startEdit = this.startEdit.bind(this);
        this.applyDeleteMessage = this.applyDeleteMessage.bind(this);
        this.isCommentEdited = this.isCommentEdited.bind(this);
        this.closeMenu = this.closeMenu.bind(this);
        this.closeMenuWithFocus = this.closeMenuWithFocus.bind(this);
        this.scrollElementIntoViewIfNeeded = this.scrollElementIntoViewIfNeeded.bind(this);
        this.renderAdjustPosition = this.renderAdjustPosition.bind(this);
    }

    componentDidUpdate(prevProps: AnnotationContentProps) {
        if (this.props.isParentScrolling === true && this.props.isParentScrolling !== prevProps.isParentScrolling) {
            this.setState({
                openMenu: false,
            });
        }

        if (
            this.props.isInlineEdit &&
            this.isCommentEdited() &&
            !this.isCommentEditedImpl(prevProps.editingAnnotation)
        ) {
            this.scrollElementIntoViewIfNeeded();
        }
    }

    scrollElementIntoViewIfNeeded() {
        requestAnimationFrame(() => {
            const domNode = this.menuRef.current;
            const parentNode = getScrollParent(domNode);
            if (
                domNode &&
                parentNode &&
                domNode.offsetHeight + domNode.offsetTop > parentNode.offsetHeight + parentNode.scrollTop
            ) {
                domNode.scrollIntoView(false);
            }
        });
    }

    onClickMenu() {
        this.setState({ openMenu: !this.state.openMenu }, () => {
            if (this.props.menuRefCallback) {
                this.props.menuRefCallback(this.menuWrapperRef);
            }
        });
    }

    onEscDown(event: React.KeyboardEvent) {
        if (this.state.openMenu) {
            event.preventDefault();
            this.closeMenuWithFocus();
        } else if (!this.props.isInlineEdit) {
            event.preventDefault();
            this.props.clearAnnotation();
        }
    }

    closeMenu() {
        this.setState({
            openMenu: false,
        });
    }

    closeMenuWithFocus() {
        this.closeMenu();
        this.menuRef.current.focus();
    }

    onMouseLeave = () => {
        if (this.props.isInlineEdit) {
            this.props.mouseLeaveAnnotation(this.props.attachmentId);
        }
    };

    onMouseEnter() {
        if (this.props.isInlineEdit) {
            this.props.mouseEnterAnnotation(this.props.attachmentId, this.props.annotation);
        }
    }

    startEdit() {
        const selection = this.props.isInlineEdit ? { showEditorInline: true } : { showEditor: true, mode: "EDITING" };
        this.props.setAnnotation({
            ...this.props.annotation,
            selection: { ...this.props.annotation.selection, ...selection },
        });
        this.setState({
            openMenu: false,
        });
    }

    applyDeleteMessage() {
        this.props.deleteAnnotation(this.props.attachmentId, this.props.annotation);
        this.props.clearAnnotation();
    }

    isCommentEdited() {
        return this.isCommentEditedImpl(this.props.editingAnnotation);
    }

    isCommentEditedImpl(editingAnnotation: AnnotationItemExtended) {
        return (
            !isEmpty(editingAnnotation) &&
            editingAnnotation.selection &&
            editingAnnotation.selection.showEditorInline &&
            editingAnnotation.annotationId === this.props.annotation.annotationId
        );
    }

    componentDidMount() {
        if (!this.props.isInlineEdit) {
            // use timeout because focus trap on Dialog.tsx render after current focus trap
            // and focus missing from content
            setTimeout(() => this.menuRef.current.focus(), 0);
        }
    }

    renderAdjustPosition(props: any) {
        return !this.props.isInlineEdit ? (
            <AdjustPosition
                ref={this.adjustPositionRef}
                baseElRef={this.fakeDotRef}
                childElRef={this.containerRef}
                adjustOptions={adjustContentOptions}
            >
                {props}
            </AdjustPosition>
        ) : (
            props
        );
    }

    render() {
        const innerClasses = [styles.inner];
        let { theme = [], active } = this.props;

        if (theme == null) {
            theme = [];
        }

        if (typeof theme === "string") {
            theme = [theme];
        }

        if (active || this.state.openMenu) {
            innerClasses.push(styles.active);
        }

        theme.forEach(classname => {
            innerClasses.push(styles[classname]);
        });

        if (this.state.openMenu) {
            innerClasses.push(styles.opened);
        }

        const { annotation, editingAnnotation, onCancel, onChange, onSubmit } = this.props;
        const { data, message } = annotation;
        const adjustElementStyles: CSSProperties = !this.props.isInlineEdit
            ? { position: "absolute", visibility: "hidden" }
            : null;

        return (
            <>
                {/* fake active dot: need to adjust from it */}
                <div className={styles.fake_dot} ref={this.fakeDotRef} />
                {this.renderAdjustPosition(
                    <WithKeyHandlers actionsOnKeyDown={[{ key: Keys.Escape, action: this.onEscDown }]}>
                        <div ref={this.containerRef} style={adjustElementStyles}>
                            <div
                                ref={this.menuRef}
                                className={classNamesCtx(...innerClasses)}
                                onMouseLeave={this.onMouseLeave}
                                onMouseEnter={this.onMouseEnter}
                                tabIndex={0}
                            >
                                {(message.canEdit || message.canDelete) && (
                                    <div ref={this.menuContainerRef} className={styles.menu_container}>
                                        <div className={styles.menu_dots} ref={this.menuOpenerRef}>
                                            <ButtonContrastIcon iconName={icon.more} action={this.onClickMenu} />
                                        </div>
                                        {this.state.openMenu &&
                                            ReactDOM.createPortal(
                                                <AdjustPosition
                                                    baseElRef={this.menuOpenerRef}
                                                    childElRef={this.menuWrapperRef}
                                                    adjustOptions={adjustMenuOptions}
                                                >
                                                    <ClickOutside
                                                        containerElRef={this.menuContainerRef}
                                                        closeActionHandler={this.closeMenu}
                                                        triggerElRef={this.menuWrapperRef}
                                                    >
                                                        <div
                                                            className={classNames([
                                                                styles.menu_wrapper,
                                                                "ko-inline-popup",
                                                            ])}
                                                            ref={this.menuWrapperRef}
                                                        >
                                                            <ImageAnnotationMenu
                                                                annotation={annotation}
                                                                applyDeleteMessageAction={this.applyDeleteMessage}
                                                                startEditAction={this.startEdit}
                                                            />
                                                        </div>
                                                    </ClickOutside>
                                                </AdjustPosition>,
                                                document.body
                                            )}
                                    </div>
                                )}
                                <div className={styles.avatar_container}>
                                    <div
                                        className={classNames(
                                            styles.annotation_avatar,
                                            "user-avatar",
                                            "avatar_40",
                                            `avatar_40_${message.userId}`
                                        )}
                                    />
                                </div>
                                <div className={styles.comment}>
                                    <div className={classNames(styles.author, "fs-block")}>{message.userName}</div>
                                    <DatePresenter
                                        date={message.date}
                                        isUtc={true}
                                        className={styles.date}
                                        disableDefaultTheme={true}
                                    />
                                    {this.isCommentEdited() ? (
                                        <AnnotationTextAreaContainer
                                            annotation={editingAnnotation}
                                            withScroll={!this.props.isInlineEdit}
                                            onCancel={onCancel}
                                            onSubmit={onSubmit}
                                            onChange={onChange}
                                        />
                                    ) : this.props.isInlineEdit ? (
                                        <FormattedMessage
                                            ref={this.commentRef}
                                            className={styles.message}
                                            text={data.text}
                                            projectOrTask={this.props.projectOrTask}
                                        />
                                    ) : (
                                        <VerticalScrollbars
                                            style={messageScrollbarStyles}
                                            translateContentSizesToHolder={true}
                                            darkTheme={true}
                                        >
                                            <FormattedMessage
                                                ref={this.commentRef}
                                                className={styles.message}
                                                text={data.text}
                                                projectOrTask={this.props.projectOrTask}
                                            />
                                        </VerticalScrollbars>
                                    )}
                                </div>
                            </div>
                        </div>
                    </WithKeyHandlers>
                )}
            </>
        );
    }
}
